<template>
  <v-dialog
    v-model="isActive"
    max-width="500"
    persistent
  >
    <v-card>
      <v-toolbar dense class="elevation-0">
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="LKReportsDataCollectionsModal__content">
        <div v-if="isLoading" class="LKReportsDataCollectionsModal__loading">
          <v-progress-circular indeterminate color="primary" />
          <p>Загрузка доступных сборов данных...</p>
        </div>

        <template v-else>
          <div class="LKReportsDataCollectionsModal__filter">
            <v-radio-group
              v-model="isOpenOnly"
              row
              dense
              hide-details
              @change="onFilterChange"
            >
              <v-radio
                label="Открытые"
                :value="true"
              />
              <v-radio
                label="Все"
                :value="false"
              />
            </v-radio-group>
          </div>

          <div v-if="dataCollections.length === 0" class="LKReportsDataCollectionsModal__empty">
            <p>Нет доступных сборов данных</p>
          </div>

          <div v-else class="LKReportsDataCollectionsModal__field">
            <div class="LKReportsDataCollectionsModal__header">
              <label class="LKReportsDataCollectionsModal__label">
                Выберите сборы данных
              </label>
              <v-checkbox
                v-model="selectAll"
                label="Выбрать все"
                dense
                hide-details
                @change="toggleSelectAll"
              />
            </div>

            <div class="LKReportsDataCollectionsModal__list">
              <div
                v-for="item in dataCollections"
                :key="item.id"
                class="LKReportsDataCollectionsModal__item"
              >
                <v-checkbox
                  v-model="selectedIds"
                  :value="item.id"
                  dense
                  hide-details
                  class="LKReportsDataCollectionsModal__checkbox"
                >
                  <template #label>
                    <div class="LKReportsDataCollectionsModal__item-content">
                      <div class="LKReportsDataCollectionsModal__item-name">
                        {{ item.name }}
                      </div>
                      <div class="LKReportsDataCollectionsModal__item-meta">
                        <span class="LKReportsDataCollectionsModal__item-id">ID: {{ item.id }}</span>
                        <span v-if="item.start_at || item.end_at" class="LKReportsDataCollectionsModal__item-dates">
                          {{ formatDateRange(item.start_at, item.end_at) }}
                        </span>
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </div>
            </div>
          </div>
        </template>
      </v-card-text>

      <v-card-actions class="LKReportsDataCollectionsModal__actions">
        <v-spacer />
        <v-btn
          text
          @click="close"
        >
          Отмена
        </v-btn>
        <v-btn
          color="primary"
          outlined
          :disabled="selectedIds.length === 0"
          @click="onSubmit"
        >
          Сформировать
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { DataCollectionsApi } from '@/api/resources/DataCollectionsApi'

const dataCollectionsApi = new DataCollectionsApi()

export default {
  name: 'LKReportsDataCollectionsModal',
  data: () => ({
    isActive: false,
    isLoading: false,
    dataCollections: [],
    selectedIds: [],
    selectAll: false,
    isOpenOnly: true,
  }),
  watch: {
    selectedIds(newVal) {
      // Обновляем состояние "Выбрать все"
      this.selectAll = newVal.length === this.dataCollections.length && this.dataCollections.length > 0
    },
  },
  methods: {
    async open() {
      await this.$nextTick()
      this.isActive = true
      await this.loadDataCollections()
    },
    close() {
      this.isActive = false
      this.selectedIds = []
      this.selectAll = false
      this.dataCollections = []
      this.isOpenOnly = true
      this.$emit('closed')
    },
    async loadDataCollections() {
      this.isLoading = true
      try {
        const response = await dataCollectionsApi.getCurated({
          params: {
            is_open: this.isOpenOnly,
          },
        })
        this.dataCollections = response.data || []
      } catch (error) {
        this.$notice.error('Ошибка при загрузке сборов данных')
        this.dataCollections = []
      } finally {
        this.isLoading = false
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedIds = this.dataCollections.map((item) => item.id)
      } else {
        this.selectedIds = []
      }
    },
    async onFilterChange() {
      // Сбрасываем выбранные элементы при смене фильтра
      this.selectedIds = []
      this.selectAll = false
      // Перезагружаем список с новым фильтром
      await this.loadDataCollections()
    },
    formatDateRange(startAt, endAt) {
      const formatDate = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      }

      const start = formatDate(startAt)
      const end = formatDate(endAt)

      if (start && end) {
        return `${start} - ${end}`
      }
      if (start) {
        return `с ${start}`
      }
      if (end) {
        return `до ${end}`
      }
      return ''
    },
    onSubmit() {
      if (this.selectedIds.length > 0) {
        // Если выбран один элемент - возвращаем как число
        // Если несколько - возвращаем как массив
        const data_collection_id = this.selectedIds.length === 1
          ? this.selectedIds[0]
          : this.selectedIds

        this.$emit('ready', { data_collection_id })
        this.close()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.LKReportsDataCollectionsModal {
  &__content {
    padding: 20px;
    min-height: 200px;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    min-height: 200px;

    p {
      margin: 0;
      color: #666;
      font-size: 14px;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;

    p {
      margin: 0;
      color: #666;
      font-size: 14px;
    }
  }

  &__field {
    margin-bottom: 20px;
  }

  &__filter {
    margin-bottom: 16px;
    padding: 12px;
    background-color: #f5f5f5;
    border-radius: 4px;

    ::v-deep .v-input--radio-group {
      margin-top: 0;
    }

    ::v-deep .v-radio {
      margin-right: 16px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  &__label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  &__list {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 4px;
    background-color: #fafafa;
  }

  &__item {
    background-color: #fff;
    border-radius: 4px;
    margin-bottom: 4px;
    padding: 6px 8px;
    transition: all 0.2s ease;
    border: 1px solid transparent;

    &:hover {
      border-color: #e0e0e0;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__item-content {
    width: 100%;
    padding-left: 4px;
  }

  &__item-name {
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin-bottom: 2px;
    line-height: 1.3;
  }

  &__item-meta {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: #666;
    line-height: 1.2;
  }

  &__item-id {
    color: #1976d2;
    font-weight: 500;
  }

  &__item-dates {
    color: #757575;
  }

  &__checkbox {
    margin: 0;
    padding: 0;

    ::v-deep .v-input--selection-controls {
      margin-top: 0;
      padding-top: 0;
    }

    ::v-deep .v-input--selection-controls__input {
      margin-right: 8px;
    }

    ::v-deep .v-input__slot {
      margin-bottom: 0;
    }

    ::v-deep .v-label {
      width: 100%;
      margin-bottom: 0;
    }

    ::v-deep .v-messages {
      display: none;
    }
  }

  &__actions {
    padding: 12px 16px;
  }
}
</style>

